import type { Category } from '../../api/schemas';
import type { SignupMode, StepId } from './draft';

/** One line under each business type, in the owner's words, so the choice is obvious at a glance. */
export const CATEGORY_HINT: Readonly<Record<Category, string>> = {
  restaurant: 'Mesas, reservas y menú',
  barbershop: 'Cortes, barba y fade',
  salon: 'Cabello, color y peinados',
  doctor: 'Consultas y citas médicas',
  florist: 'Arreglos y entregas',
  autoshop: 'Mantenimiento y reparación',
  gym: 'Membresías y entrenamiento',
  nailstudio: 'Manicure y pedicure',
  spa: 'Masajes y tratamientos',
  classstudio: 'Pilates, yoga y más',
  dogwalker: 'Paseos y cuidado',
  cleaner: 'Casas y oficinas',
  plumber: 'Instalaciones y fugas',
  electrician: 'Instalaciones y averías',
  handyman: 'Arreglos en casa',
  tutor: 'Clases particulares',
  trainingcenter: 'Cursos y talleres',
};

/** The sentence under each question: why Dale asks, never a lecture. */
export const STEP_HINT: Readonly<Partial<Record<StepId, string>>> = {
  nombre: 'Así te van a encontrar tus clientes en Dale.',
  tipo: 'Con esto te mostramos a quien busca lo que haces.',
  zona: 'La gente reserva cerca. Tu zona te pone en su mapa.',
  lugar: 'Toca el mapa en tu entrada. Así te encuentran tus clientes.',
  telefono: 'Opcional. Lo ven tus clientes si necesitan llamarte.',
  dueno: 'Para saber con quién hablamos.',
  correo: 'Con este correo entras a tu panel.',
  clave: 'Mínimo 12 caracteres. Una frase corta funciona muy bien.',
  horario: 'Lo trajimos de Google. Cámbialo si algo no cuadra.',
  plan: 'Lo cambias cuando quieras desde tu panel.',
};

/** What an independent does, said the way they say it about themselves. */
export const INDEPENDENT_TRADE: Readonly<Partial<Record<Category, { label: string; hint: string }>>> = {
  barbershop: { label: 'Barbero', hint: 'Cortes, barba y fade' },
  salon: { label: 'Estilista', hint: 'Cabello, color y peinados' },
  nailstudio: { label: 'Manicurista', hint: 'Manicure y pedicure' },
  spa: { label: 'Masajista o terapeuta', hint: 'Masajes y tratamientos' },
  classstudio: { label: 'Instructor de clases', hint: 'Pilates, yoga, baile' },
  gym: { label: 'Entrenador personal', hint: 'Sesiones y planes' },
  tutor: { label: 'Profesor particular', hint: 'Clases y tutorías' },
  dogwalker: { label: 'Paseador de perros', hint: 'Paseos y cuidado' },
  cleaner: { label: 'Limpieza', hint: 'Casas y oficinas' },
  plumber: { label: 'Plomero', hint: 'Instalaciones y fugas' },
  electrician: { label: 'Electricista', hint: 'Instalaciones y averías' },
  handyman: { label: 'Técnico de arreglos', hint: 'Arreglos en casa' },
  doctor: { label: 'Profesional de salud', hint: 'Consultas y citas' },
};

/** The independent signup asks the same things in their own words. */
export const INDEPENDENT_HINT: Readonly<Partial<Record<StepId, string>>> = {
  nombre: 'Tu nombre o tu marca. Así te buscan y te reservan en Dale.',
  tipo: 'Con esto te mostramos a quien busca lo que haces.',
  zona: 'La gente reserva cerca. Tu zona te pone en su mapa.',
  lugar: 'Donde atiendes. Si vas a domicilio, marca tu zona de base.',
  telefono: 'Opcional. Lo ven tus clientes si necesitan escribirte.',
  plan: 'Si un día armas equipo o abres local, pasas a un plan de negocio desde tu panel.',
};

/** Who the independent signup is for, said once at the top so nobody signs up on the wrong side. */
export const INDEPENDENT_WHO =
  'Para quien trabaja por su cuenta, sin empleados ni local con equipo: barberos, manicuristas, entrenadores, profesores, técnicos.';

export function hintFor(mode: SignupMode, step: StepId): string | undefined {
  return mode === 'independiente' ? (INDEPENDENT_HINT[step] ?? STEP_HINT[step]) : STEP_HINT[step];
}
